import settings from '@/constants/constants';
import useClickAndCollectLocationQueryHistory from '@/hooks/order/useClickAndCollectLocationQueryHistory';
import { createAddressFromData, formatAddressString } from '@/utils/address';
import type { Address } from '@montugroup/circuit-shared/types/address';
import type { AddressDataType } from '@montugroup/design-system';
import { Box, LocationInput } from '@montugroup/design-system';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

export type ShippingAddressProps = {
  address: Address;
  onSelectedAddress: (address: Address) => void;
};

const ShippingDetailsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const LocationInputWrapper = styled(Box)(() => ({
  '& .MuiInputBase-input': {
    height: 44,
    paddingTop: 12,
    paddingBottom: 12,
    boxSizing: 'border-box',
  },
}));

function ClickAndCollectionLocationQuery({ address, onSelectedAddress }: ShippingAddressProps) {
  const {
    get: getLocationQueryHistory,
    add: addLocationToHistory,
    remove: removeLocationFromHistory,
  } = useClickAndCollectLocationQueryHistory();
  const [locationQueryHistory, setLocationQueryHistory] = useState<string[]>(getLocationQueryHistory());
  const displayValue = formatAddressString(address);

  const handleAddressSelected = async (addressData: AddressDataType) => {
    if (addressData.geometry?.location) {
      const asAddress = createAddressFromData(addressData);
      onSelectedAddress(asAddress);
      addLocationToHistory(formatAddressString(asAddress));
      setLocationQueryHistory(getLocationQueryHistory());
    }
  };

  const handleRemoveHistoryItem = (item: string) => {
    removeLocationFromHistory(item);
    setLocationQueryHistory(getLocationQueryHistory());
  };

  return (
    <ShippingDetailsContainer>
      <LocationInputWrapper>
        <LocationInput
          variant="outlined"
          value={displayValue}
          shrinkLabel
          name="fullAddress"
          id="collection-input"
          icon={<LocationOnOutlinedIcon />}
          googlePlacesApiKey={settings.googlePlacesApiKey}
          onSelectedAddress={handleAddressSelected}
          defaultAddressOptions={locationQueryHistory}
          onDefaultAddressOptionClear={handleRemoveHistoryItem}
          includeLocalities
          disableClearable
        />
        <FormHelperText>Changing your address will refresh options</FormHelperText>
      </LocationInputWrapper>
    </ShippingDetailsContainer>
  );
}

export default ClickAndCollectionLocationQuery;
